import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type {
  CrmAccountListItem,
  CrmRevenueReport,
  CrmRevenueRow,
  CrmRevenueStatus,
  Timestamp,
} from "@shared/modules/crm";
import { api } from "../../../core/lib/api";
import { formatDateTime, toDate } from "../../../core/lib/format";
import { useToastStore } from "../../../core/stores/toast";
import { PageHeader } from "../../../core/components/ui/page-header";
import { Badge } from "../../../core/components/ui/badge";
import { Button } from "../../../core/components/ui/button";
import { Card } from "../../../core/components/ui/card";
import { Field, Input, Select } from "../../../core/components/ui/input";
import { LabelOptions } from "../components/label-options";
import { Money } from "../components/money";
import {
  CRM_OPTIONS,
  CRM_PARTNER_LABELS,
  CRM_REVENUE_KIND_LABELS,
  CRM_REVENUE_STATUS_LABELS,
  formatCents,
  partnerLabel,
} from "../lib/crm";

const STATUS_VARIANT: Record<CrmRevenueStatus, Parameters<typeof Badge>[0]["variant"]> = {
  prevu: "neutral",
  facture: "warning",
  encaisse: "success",
  annule: "danger",
};

const CSV_HEADER = [
  "Début",
  "Fin",
  "Dossier",
  "Partenaire",
  "Nature",
  "Libellé",
  "Montant",
  "Commission",
  "Net",
  "Devise",
  "Statut",
  "Facture",
];

export function CrmRevenues() {
  const push = useToastStore((s) => s.push);

  const initialRange = useMemo(defaultRange, []);
  const [from, setFrom] = useState(initialRange.from);
  const [to, setTo] = useState(initialRange.to);
  const [status, setStatus] = useState<CrmRevenueStatus | "all">("all");
  const [partner, setPartner] = useState("all");
  const [accountId, setAccountId] = useState("all");

  const [accounts, setAccounts] = useState<CrmAccountListItem[]>([]);
  const [report, setReport] = useState<CrmRevenueReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    api<CrmAccountListItem[]>("/api/crm/accounts")
      .then(setAccounts)
      .catch(() => setAccounts([]));
  }, []);

  const query = useMemo(() => {
    const params = new URLSearchParams();
    const fromIso = dayBoundaryIso(from, "start");
    if (fromIso) params.set("from", fromIso);
    const toIso = dayBoundaryIso(to, "end");
    if (toIso) params.set("to", toIso);
    if (status !== "all") params.set("status", status);
    if (partner !== "all") params.set("partner", partner);
    if (accountId !== "all") params.set("accountId", accountId);
    return params.toString();
  }, [from, to, status, partner, accountId]);

  useEffect(() => {
    // Les filtres s'enchaînent vite : on ignore la réponse d'une requête devenue obsolète.
    let cancelled = false;
    setLoading(true);
    api<CrmRevenueReport>(`/api/crm/revenues${query ? `?${query}` : ""}`)
      .then((r) => {
        if (cancelled) return;
        setReport(r);
        setFailed(false);
      })
      .catch(() => {
        if (cancelled) return;
        setReport(null);
        setFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [query]);

  const rows = report?.rows ?? [];
  const totals = report?.totals ?? {
    amountCents: 0,
    commissionCents: 0,
    netCents: 0,
    retainedCount: 0,
  };
  const cancelledCount = rows.length - totals.retainedCount;

  const summary = useMemo(() => {
    let invoicedCents = 0;
    let collectedCents = 0;
    let commissionCents = 0;
    for (const row of rows) {
      if (row.status === "facture" || row.status === "encaisse") invoicedCents += row.amountCents;
      if (row.status === "encaisse") {
        collectedCents += row.amountCents;
        commissionCents += row.commissionCents;
      }
    }
    return {
      invoicedCents,
      collectedCents,
      commissionCents,
      netCents: collectedCents - commissionCents,
    };
  }, [rows]);

  const accountOptions = useMemo(
    () => [...accounts].sort((a, b) => a.name.localeCompare(b.name, "fr-CA")),
    [accounts],
  );

  const resetFilters = () => {
    const range = defaultRange();
    setFrom(range.from);
    setTo(range.to);
    setStatus("all");
    setPartner("all");
    setAccountId("all");
  };

  const exportCsv = () => {
    if (rows.length === 0) return;
    const lines = [CSV_HEADER, ...rows.map(csvRow)].map((cells) => cells.map(csvCell).join(","));
    // BOM : sans lui, Excel lit le CSV en latin-1 et casse les accents.
    const blob = new Blob([`\ufeff${lines.join("\r\n")}`], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `revenus-crm-${from}-${to}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
    push("success", "Export CSV généré", `${rows.length} ligne${rows.length > 1 ? "s" : ""}`);
  };

  return (
    <div className="page">
      <PageHeader
        title="Revenus & commissions"
        subtitle="Les lignes de revenus datées, leur commission et le net après commission."
        actions={
          <Button variant="ghost" onClick={exportCsv} disabled={rows.length === 0}>
            Exporter CSV
          </Button>
        }
      />

      <div className="crm-toolbar">
        <div className="crm-toolbar__filters">
          <Field label="Du">
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </Field>
          <Field label="Au">
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </Field>
          <Field label="Statut">
            <Select
              value={status}
              onChange={(e) => setStatus(e.target.value as CrmRevenueStatus | "all")}
            >
              <option value="all">Tous</option>
              <LabelOptions labels={CRM_REVENUE_STATUS_LABELS} />
            </Select>
          </Field>
          <Field label="Partenaire">
            <Select value={partner} onChange={(e) => setPartner(e.target.value)}>
              <option value="all">Tous</option>
              <LabelOptions labels={CRM_PARTNER_LABELS} />
            </Select>
          </Field>
          <Field label="Dossier">
            <Select value={accountId} onChange={(e) => setAccountId(e.target.value)}>
              <option value="all">Tous</option>
              {accountOptions.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        <div className="crm-toolbar__spacer" />
        <Button variant="ghost" size="sm" onClick={resetFilters}>
          Réinitialiser
        </Button>
      </div>

      <div className="crm-stats">
        <div className="crm-stat">
          <span className="crm-stat__label">Total facturé</span>
          <span className="crm-stat__value">{formatCents(summary.invoicedCents)}</span>
          <span className="crm-stat__hint">Lignes facturées ou encaissées</span>
        </div>
        <div className="crm-stat">
          <span className="crm-stat__label">Encaissé</span>
          <span className="crm-stat__value">{formatCents(summary.collectedCents)}</span>
          <span className="crm-stat__hint">Lignes réglées sur la période</span>
        </div>
        <div className="crm-stat">
          <span className="crm-stat__label">Commission due</span>
          <span className="crm-stat__value">{formatCents(summary.commissionCents)}</span>
          <span className="crm-stat__hint">Part partenaire sur l'encaissé</span>
        </div>
        <div className="crm-stat crm-stat--accent">
          {/* Le tableau a aussi une colonne « Net » (montant − commission, ligne à
              ligne) : la tuile précise donc sur quoi elle porte. */}
          <span className="crm-stat__label">Net sur l'encaissé</span>
          <span className="crm-stat__value">{formatCents(summary.netCents)}</span>
          <span className="crm-stat__hint">Encaissé moins commission due</span>
        </div>
        <div className="crm-stat">
          <span className="crm-stat__label">Lignes</span>
          <span className="crm-stat__value">{rows.length}</span>
          <span className="crm-stat__hint">Sur la période sélectionnée</span>
        </div>
      </div>

      <Card
        title="Revenus par mois"
        subtitle="Barre claire : montant retenu. Barre pleine : part de commission. Lignes annulées exclues."
      >
        <MonthlyBars months={report?.byMonth ?? []} />
      </Card>

      <table className="table">
        <thead>
          <tr>
            <th>Période</th>
            <th>Dossier</th>
            <th>Partenaire</th>
            <th>Nature</th>
            <th>Libellé</th>
            <th>Montant</th>
            <th>Commission</th>
            <th>Net</th>
            <th>Statut</th>
            <th>Facture</th>
          </tr>
        </thead>
        <tbody>
          {loading && !report ? (
            <tr>
              <td colSpan={10} className="table__empty">
                Chargement des revenus…
              </td>
            </tr>
          ) : failed ? (
            <tr>
              <td colSpan={10} className="table__empty">
                Revenus indisponibles — réessayez dans un instant.
              </td>
            </tr>
          ) : rows.length === 0 ? (
            <tr>
              <td colSpan={10} className="table__empty">
                Aucune ligne de revenu sur ces filtres.
              </td>
            </tr>
          ) : (
            <>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td>{periodLabel(row)}</td>
                  <td>
                    <Link to={`/crm/dossiers/${row.accountId}`}>{row.accountName}</Link>
                  </td>
                  <td>{partnerLabel(row.accountPartner)}</td>
                  <td>{CRM_REVENUE_KIND_LABELS[row.kind]}</td>
                  <td>{row.label}</td>
                  <td>
                    <Money cents={row.amountCents} currency={row.currency} />
                  </td>
                  <td>
                    <Money cents={row.commissionCents} currency={row.currency} />
                  </td>
                  <td>
                    <Money cents={row.amountCents - row.commissionCents} currency={row.currency} />
                  </td>
                  <td>
                    <Badge variant={STATUS_VARIANT[row.status]}>
                      {CRM_REVENUE_STATUS_LABELS[row.status]}
                    </Badge>
                  </td>
                  <td className="crm-mono">{row.invoiceRef ?? "—"}</td>
                </tr>
              ))}
              {/* Totaux dans le tbody : .table ne stylise que `thead th` et `tbody td`,
                  un vrai <tfoot> sortirait sans marges ni séparateur. */}
              <tr>
                <td colSpan={5}>
                  <strong>
                    Total — {totals.retainedCount} ligne{totals.retainedCount > 1 ? "s" : ""}
                  </strong>
                  {cancelledCount > 0 ? (
                    <span className="crm-chip crm-chip--muted">
                      {cancelledCount} annulée{cancelledCount > 1 ? "s" : ""} exclue
                      {cancelledCount > 1 ? "s" : ""}
                    </span>
                  ) : null}
                </td>
                <td>
                  <Money cents={totals.amountCents} currency={CRM_OPTIONS.currency} strong />
                </td>
                <td>
                  <Money cents={totals.commissionCents} currency={CRM_OPTIONS.currency} strong />
                </td>
                <td>
                  <Money cents={totals.netCents} currency={CRM_OPTIONS.currency} strong />
                </td>
                <td colSpan={2} />
              </tr>
            </>
          )}
        </tbody>
      </table>
    </div>
  );
}

/* ---------------- Série mensuelle ---------------- */

function MonthlyBars({ months }: { months: CrmRevenueReport["byMonth"] }) {
  if (months.length === 0) {
    return <p className="crm-empty">Aucun revenu sur la période sélectionnée.</p>;
  }

  const width = 720;
  const height = 168;
  const padTop = 12;
  const padBottom = 24;
  const plot = height - padTop - padBottom;
  // Les ajustements peuvent être négatifs : la ligne de zéro flotte selon l'amplitude réelle.
  const max = Math.max(0, ...months.map((m) => m.amountCents));
  const min = Math.min(0, ...months.map((m) => m.amountCents));
  const span = max - min || 1;
  const yOf = (cents: number) => padTop + ((max - cents) / span) * plot;
  const zeroY = yOf(0);
  const slot = width / months.length;
  const barWidth = Math.min(26, slot * 0.5);
  const labelStep = Math.max(1, Math.ceil(months.length / 12));

  return (
    // currentColor : le graphique suit la couleur de texte du thème, en jouant
    // sur l'opacité via les classes de la feuille du module.
    <svg
      className="crm-chart"
      viewBox={`0 0 ${width} ${height}`}
      width="100%"
      role="img"
      aria-label="Revenus par mois"
    >
      <line className="crm-chart__axis" x1={0} x2={width} y1={zeroY} y2={zeroY} />
      {months.map((month, index) => {
        const center = slot * (index + 0.5);
        const amountY = yOf(month.amountCents);
        const commissionY = yOf(month.commissionCents);
        return (
          <g key={month.month}>
            <rect
              className="crm-chart__amount"
              x={center - barWidth / 2}
              y={Math.min(zeroY, amountY)}
              width={barWidth}
              height={barHeight(amountY, zeroY, month.amountCents)}
            />
            <rect
              className="crm-chart__commission"
              x={center - barWidth / 4}
              y={Math.min(zeroY, commissionY)}
              width={barWidth / 2}
              height={barHeight(commissionY, zeroY, month.commissionCents)}
            />
            {index % labelStep === 0 ? (
              <text className="crm-chart__label" x={center} y={height - 6} textAnchor="middle">
                {monthLabel(month.month, index === 0)}
              </text>
            ) : null}
          </g>
        );
      })}
    </svg>
  );
}

/** Un mois non nul garde au moins un pixel : une barre invisible se lit comme une donnée absente. */
function barHeight(valueY: number, zeroY: number, cents: number): number {
  const raw = Math.abs(valueY - zeroY);
  return cents === 0 ? 0 : Math.max(raw, 1);
}

function monthLabel(month: string, withYear: boolean): string {
  const parts = month.split("-");
  const year = Number(parts[0] ?? "");
  const index = Number(parts[1] ?? "") - 1;
  if (!Number.isInteger(year) || !Number.isInteger(index) || index < 0 || index > 11) return month;
  const short = new Date(year, index, 1).toLocaleDateString("fr-CA", { month: "short" });
  return withYear ? `${short} ${String(year).slice(-2)}` : short;
}

/* ---------------- Utilitaires ---------------- */

/** Fenêtre par défaut : du 1er du mois, onze mois en arrière, à aujourd'hui. */
function defaultRange(): { from: string; to: string } {
  const now = new Date();
  return {
    from: toDateInput(new Date(now.getFullYear(), now.getMonth() - 11, 1)),
    to: toDateInput(now),
  };
}

function toDateInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * "2026-08-10" -> ISO complet. On envoie les bornes locales (début / fin de journée)
 * pour que le dernier jour saisi reste inclus quel que soit le fuseau du serveur.
 */
function dayBoundaryIso(value: string, edge: "start" | "end"): string | null {
  if (!value) return null;
  const d = new Date(`${value}T${edge === "start" ? "00:00:00.000" : "23:59:59.999"}`);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

function periodLabel(row: CrmRevenueRow): string {
  const opts: Intl.DateTimeFormatOptions = { day: "numeric", month: "short", year: "numeric" };
  const start = formatDateTime(row.periodStart, opts);
  return row.periodEnd ? `${start} → ${formatDateTime(row.periodEnd, opts)}` : start;
}

/* ---------------- Export CSV ---------------- */

function csvRow(row: CrmRevenueRow): string[] {
  return [
    csvDate(row.periodStart),
    csvDate(row.periodEnd),
    row.accountName,
    partnerLabel(row.accountPartner),
    CRM_REVENUE_KIND_LABELS[row.kind],
    row.label,
    csvAmount(row.amountCents),
    csvAmount(row.commissionCents),
    csvAmount(row.amountCents - row.commissionCents),
    row.currency,
    CRM_REVENUE_STATUS_LABELS[row.status],
    row.invoiceRef ?? "",
  ];
}

/** Dates ISO et montants à point décimal : le fichier doit rester exploitable dans un tableur. */
function csvDate(value: Timestamp | null): string {
  const d = toDate(value);
  return d ? toDateInput(d) : "";
}

function csvAmount(cents: number): string {
  return (cents / 100).toFixed(2);
}

function csvCell(value: string): string {
  return /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}
